import { OpenAPI } from '@beeboard/common';
import { useI18n } from 'vue-i18n';
import { useProfileStore } from '@/store/profileStore';
import { getLocale } from '@/utils/i18n/vueI18n';
import { parseSSEBuffer } from '@/utils/sse';

export interface ChatMsg {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  isStreaming?: boolean;
  isError?: boolean;
  isStopped?: boolean;
  statusText?: 'queued' | 'inspecting';
}

const STORAGE_KEY_PREFIX = 'help_chat_session';
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_STORED_MESSAGES = 20;
const MAX_VISIBLE_MESSAGES = 40;
const DEFAULT_RETRY_AFTER_SECONDS = 30;
const STREAM_TIMEOUT_MS = 30_000;

interface StoredSession {
  sessionId: string;
  messages: ChatMsg[];
  savedAt: number;
}

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}:${userId}`;
}

/** Remove every persisted help-chat session (all users). Called on logout. */
export function clearHelpChatStorage(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* storage unavailable */
  }
}

function newSession(): StoredSession {
  return { sessionId: crypto.randomUUID(), messages: [], savedAt: Date.now() };
}

function loadSession(userId: string): StoredSession {
  if (!userId) {
    return newSession();
  }
  try {
    const raw = sessionStorage.getItem(storageKey(userId));
    if (raw) {
      const parsed = JSON.parse(raw) as StoredSession;
      if (
        typeof parsed.sessionId === 'string' &&
        Array.isArray(parsed.messages) &&
        Date.now() - (parsed.savedAt ?? 0) < SESSION_TTL_MS
      ) {
        return {
          ...parsed,
          messages: parsed.messages
            .slice(-MAX_STORED_MESSAGES)
            .map((m) => ({ ...m, id: m.id || crypto.randomUUID() })),
        };
      }
      sessionStorage.removeItem(storageKey(userId));
    }
  } catch {
    /* ignore corrupt storage */
  }

  return newSession();
}

export class HelpChatHttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly retryAfterSeconds?: number,
  ) {
    super(`Help chat request failed with HTTP ${status}`);
  }
}

export class HelpChatIncompleteError extends Error {
  constructor() {
    super('Help chat stream ended without a done event');
  }
}

function parseRetryAfter(header: string | null): number {
  if (header) {
    const seconds = Number(header.trim());
    if (Number.isFinite(seconds) && seconds >= 0) {
      return Math.ceil(seconds);
    }
    const date = Date.parse(header);
    if (!Number.isNaN(date)) {
      return Math.max(0, Math.ceil((date - Date.now()) / 1000));
    }
  }

  return DEFAULT_RETRY_AFTER_SECONDS;
}

type HelpStreamEvent =
  | { type: 'text'; text: string }
  | { type: 'status'; status: 'queued' | 'inspecting' }
  | { type: 'reset' };

async function* streamHelpChat(
  body: Record<string, unknown>,
  signal: AbortSignal,
): AsyncGenerator<HelpStreamEvent> {
  let token: string | undefined;
  if (typeof OpenAPI.TOKEN === 'function') {
    token = await (OpenAPI.TOKEN as () => Promise<string>)();
  } else if (OpenAPI.TOKEN) {
    token = OpenAPI.TOKEN as string;
  }

  const response = await fetch(`${OpenAPI.BASE}/v1/ai/help/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    credentials: OpenAPI.WITH_CREDENTIALS ? 'include' : 'same-origin',
    signal,
  });

  if (response.status === 429) {
    throw new HelpChatHttpError(
      429,
      parseRetryAfter(response.headers.get('Retry-After')),
    );
  }
  if (!response.ok) {
    throw new HelpChatHttpError(response.status);
  }
  if (!response.body) {
    throw new Error('No response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const readWithTimeout = (): Promise<ReadableStreamReadResult<Uint8Array>> => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('Help chat stream timed out')),
        STREAM_TIMEOUT_MS,
      );
    });

    return Promise.race([reader.read(), timeout]).finally(() =>
      clearTimeout(timeoutId),
    );
  };

  try {
    let eof = false;
    while (!eof) {
      const { done, value } = await readWithTimeout();
      eof = done;
      buffer += eof
        ? decoder.decode()
        : decoder.decode(value, { stream: true });
      const { parsed, remaining } = parseSSEBuffer(buffer, eof);
      buffer = remaining;

      for (const { event, data } of parsed) {
        if (event === 'text_delta') {
          if (typeof data.text === 'string' && data.text) {
            yield { type: 'text', text: data.text };
          }
        } else if (event === 'status') {
          if (data.type === 'queued' || data.type === 'inspecting') {
            yield { type: 'status', status: data.type };
          }
        } else if (event === 'reset') {
          yield { type: 'reset' };
        } else if (event === 'error') {
          throw new Error(
            typeof data.message === 'string' ? data.message : 'Unknown error',
          );
        } else if (event === 'done') {
          return;
        }
      }
    }
    throw new HelpChatIncompleteError();
  } finally {
    reader.cancel().catch(() => undefined);
  }
}

export function useHelpChat() {
  const { t } = useI18n();
  const profileStore = useProfileStore();
  const userId = (): string => profileStore.profile?.id ?? '';

  const stored = loadSession(userId());
  const messages = ref<ChatMsg[]>(stored.messages);
  let sessionId = stored.sessionId;
  const isLoading = ref(false);
  const isUnavailable = ref(false);
  const rateLimitedUntil = ref(0);
  const now = ref(Date.now());
  let rateLimitTimer: ReturnType<typeof setInterval> | null = null;

  let current: { controller: AbortController; msg: ChatMsg } | null = null;

  const rateLimitSecondsLeft = computed(() =>
    Math.max(0, Math.ceil((rateLimitedUntil.value - now.value) / 1000)),
  );
  const canSend = computed(
    () =>
      !isLoading.value &&
      !isUnavailable.value &&
      rateLimitSecondsLeft.value === 0,
  );

  function saveSession() {
    const id = userId();
    if (!id) {
      return;
    }
    try {
      const data: StoredSession = {
        sessionId,
        messages: messages.value
          .filter((m) => !m.isStreaming)
          .slice(-MAX_STORED_MESSAGES),
        savedAt: Date.now(),
      };
      sessionStorage.setItem(storageKey(id), JSON.stringify(data));
    } catch {
      /* ignore storage quota errors */
    }
  }

  function startRateLimit(seconds: number) {
    rateLimitedUntil.value = Date.now() + seconds * 1000;
    now.value = Date.now();
    if (rateLimitTimer) {
      clearInterval(rateLimitTimer);
    }
    rateLimitTimer = setInterval(() => {
      now.value = Date.now();
      if (rateLimitSecondsLeft.value === 0 && rateLimitTimer) {
        clearInterval(rateLimitTimer);
        rateLimitTimer = null;
      }
    }, 1000);
  }

  function finishAsStopped(msg: ChatMsg) {
    msg.isStreaming = false;
    msg.statusText = undefined;
    msg.isStopped = true;
  }

  function abortCurrent() {
    if (!current) {
      return;
    }
    const { controller, msg } = current;
    current = null;
    finishAsStopped(msg);
    controller.abort();
  }

  function errorText(err: unknown): string {
    if (err instanceof HelpChatHttpError) {
      if (err.status === 429) {
        return t('help.ai.rateLimited', {
          seconds: err.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS,
        });
      }
      if (err.status === 403) {
        return t('help.ai.unavailable');
      }
    }
    if (err instanceof HelpChatIncompleteError) {
      return t('help.ai.interrupted');
    }

    return t('help.ai.error');
  }

  async function sendMessage(rawText: string): Promise<void> {
    const text = rawText.trim();
    if (!text || !canSend.value) {
      return;
    }

    isLoading.value = true;
    messages.value.push({ id: crypto.randomUUID(), role: 'user', text });
    messages.value.push({
      id: crypto.randomUUID(),
      role: 'assistant',
      text: '',
      isStreaming: true,
    });
    if (messages.value.length > MAX_VISIBLE_MESSAGES) {
      messages.value.splice(0, messages.value.length - MAX_VISIBLE_MESSAGES);
    }
    const msg = messages.value[messages.value.length - 1];
    const controller = new AbortController();
    current = { controller, msg };

    try {
      for await (const event of streamHelpChat(
        {
          message: text,
          language: getLocale(),
          current_route: window.location.pathname,
          session_id: sessionId,
        },
        controller.signal,
      )) {
        if (controller.signal.aborted) {
          break;
        }
        if (event.type === 'text') {
          msg.statusText = undefined;
          msg.text += event.text;
        } else if (event.type === 'status') {
          msg.statusText = event.status;
        } else {
          msg.text = '';
          msg.statusText = 'inspecting';
        }
      }

      if (!controller.signal.aborted) {
        msg.isStreaming = false;
        if (!msg.text) {
          msg.text = t('help.ai.error');
          msg.isError = true;
        }
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        msg.text = errorText(err);
        msg.isStreaming = false;
        msg.statusText = undefined;
        msg.isError = true;
        if (err instanceof HelpChatHttpError) {
          if (err.status === 429) {
            startRateLimit(
              err.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS,
            );
          } else if (err.status === 403) {
            isUnavailable.value = true;
          }
        }
      }
    } finally {
      if (current?.controller === controller) {
        current = null;
      }
      isLoading.value = false;
      saveSession();
    }
  }

  function stopStream() {
    abortCurrent();
  }

  function clearSession() {
    abortCurrent();
    messages.value = [];
    sessionId = crypto.randomUUID();
    const id = userId();
    if (id) {
      try {
        sessionStorage.removeItem(storageKey(id));
      } catch {
        /* ignore */
      }
    }
  }

  onScopeDispose(() => {
    abortCurrent();
    if (rateLimitTimer) {
      clearInterval(rateLimitTimer);
    }
    saveSession();
  });

  return {
    messages,
    isLoading,
    isUnavailable,
    rateLimitSecondsLeft,
    canSend,
    sendMessage,
    stopStream,
    clearSession,
  };
}
